import { DataEntry, GetLedgerList, GetQualityIssues, LedgerAccount } from '../types';
import { groupBy } from './groupBy';

const ledgerAccountCategoryTypes = {
  QUALITY_ISSUES: 'QUALITY_ISSUES',
  BALANCE_SHEET: 'BALANCE_SHEET',
  INCOME_STATEMENT: 'INCOME_STATEMENT',
};

export const ledgerAccountCategoryTypeMap = {
  '0': ledgerAccountCategoryTypes.QUALITY_ISSUES,
  // Assets
  '1': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Liabilities
  '3': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Equity
  '5': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Revenues
  '6': ledgerAccountCategoryTypes.INCOME_STATEMENT,
  // Cost of Sales
  '8': ledgerAccountCategoryTypes.INCOME_STATEMENT,
  // Operating Expenses
  '7': ledgerAccountCategoryTypes.INCOME_STATEMENT,
};

// ledgers in Liabilities, Equity, and Revenue have the signs reversed for their balances
const REVERSED_CATEGORIES = ['3', '5', '6'];

export const isBalanceSheet = (ledgerAccount: LedgerAccount) => {
  const ledgerAccountCategoryId = ledgerAccount.category && ledgerAccount.category.id;
  return (
    ledgerAccountCategoryTypeMap[ledgerAccountCategoryId] ===
    ledgerAccountCategoryTypes.BALANCE_SHEET
  );
};

export const areBalancesReversed = (ledgerAccount: LedgerAccount) => {
  const ledgerAccountCategoryId = ledgerAccount.category && ledgerAccount.category.id;
  return REVERSED_CATEGORIES.includes(ledgerAccountCategoryId);
};

export const ledgerAccountCategoryOrderingMap = {
  // Quality Issues
  0: '0',
  // Assets
  1: '1',
  // Liabilities
  2: '3',
  // Equity
  3: '5',
  // Revenues
  4: '6',
  // Cost of Sales
  5: '8',
  // Operating Expenses
  6: '7',
};

const normalizeString = str => String(str).trim().toLowerCase();
const ledgerAccountContainsSearchString = search => {
  const searchString = normalizeString(search);
  return ({ details, internalNotes }) => {
    if (normalizeString(details).includes(searchString)) {
      return true;
    }
    if (internalNotes.some(({ body }) => normalizeString(body).includes(searchString))) {
      return true;
    }
    // All the searches above failed to locate the search string
    return false;
  };
};

export function filterAndGroupLedgersByCategory(
  ledgerList: GetLedgerList,
  showAccounts: boolean,
  search: string,
  qualityIssuesSummary?: DataEntry[],
) {
  const { ledgerAccountsByClient } = ledgerList;

  const filterZeroEntryLedgers = Boolean(search);
  const ledgerAccountSearch = ledgerAccountContainsSearchString(search);
  const filtered =
    ledgerAccountsByClient.filter(ledger => {
      if (!showAccounts && ledger.role !== null) return false;
      if (filterZeroEntryLedgers && ledgerAccountSearch(ledger)) return true;
      if (filterZeroEntryLedgers && ledger.dataEntries.pageInfo.total === 0) return false;
      if (
        ledgerAccountCategoryTypeMap[ledger.category.id] ===
        ledgerAccountCategoryTypes.QUALITY_ISSUES
      )
        return true;
      // If ledger is a balance sheet ledger and it has no balances or data entries, filter it
      // If ledger is an income statement ledger and it has no in period balance or data entries, filter it
      const isBalanceSheet =
        ledgerAccountCategoryTypeMap[ledger.category.id] ===
        ledgerAccountCategoryTypes.BALANCE_SHEET;
      const closingOpeningBalance =
        ledger.accountingPeriodBalance.closingBalance +
        ledger.accountingPeriodBalance.openingBalance;
      const inPeriodBalance = ledger.accountingPeriodBalance.inPeriodBalance;
      const entryTotals = ledger.dataEntries.pageInfo.total;
      return isBalanceSheet
        ? closingOpeningBalance + entryTotals !== 0
        : inPeriodBalance + entryTotals !== 0;
    }) || [];

  const grouped = groupBy(filtered, ledger => ledger.category.id);

  // Put the grouped ledgers in the order of the ledgerAccountCategoryOrderingMap
  let finalOrderedLedgers: LedgerAccount[] = [];
  for (const key of Object.values(ledgerAccountCategoryOrderingMap)) {
    if (grouped[key]) {
      finalOrderedLedgers = [...finalOrderedLedgers, ...grouped[key]];
    }
  }

  const ledgerIdToIndexMap = finalOrderedLedgers.reduce((acc, ledger, index) => {
    acc[ledger.id] = index;
    return acc;
  }, {} as Record<string, number>);

  return {
    ledgers: finalOrderedLedgers,
    ledgerIdToIndexMap,
    grouped,
  };
}
/*Creating a summary object that matches the ledgerAccounts allows the summary to be easily displayed as a ledger
  table and be easily included within the ledger list in the left-side nav */
function getQualityIssuesAsLedgerAccount(qualityIssuesSummary: DataEntry[] = []): LedgerAccount {
  return {
    id: '0',
    active: true,
    accountingPeriodBalance: {
      closingBalance: 0,
      id: 'qualityIssuesLedger-0',
      inPeriodBalance: 0,
      openingBalance: 0,
    },
    category: {
      id: '0',
      name: 'Quality Issues Summary',
    },
    code: '',
    dataEntries: {
      dataEntries: qualityIssuesSummary,
      pageInfo: {
        total: qualityIssuesSummary.length,
        hasNextPage: false,
        hasPrevPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
    details: '',
    internalNotes: [],
    name: 'Quality Issues Summary',
    role: null,
    sortOrder: 1,
  };
}
